// Phase 11 — Settings panel for model-document templates.
//
// A template is a logical record (code/name/kind/optional targetSubfolder)
// with per-language .docx file variants (NL/FR/EN). The lawyer uploads a
// Word file authored with the canonical {markers}; the reference list below
// documents every marker the resolver supports so they can be copied into
// Word. Mutations require lateral.settingsBusiness (enforced server-side too).

import React, { useState, useEffect } from "react";
import { useTranslation } from "../i18n";
import { usePermissions } from "../hooks/usePermissions";
import {
  getDocTemplates,
  createDocTemplate,
  updateDocTemplate,
  deleteDocTemplate,
  uploadDocTemplateVariant,
  deleteDocTemplateVariant,
  downloadDocTemplateVariant, // 204 §E
  getDocTemplateVariables,
  getFirmSettings,
} from "../api";
import {
  FileText,
  Upload,
  Trash2,
  ChevronDown,
  ChevronRight,
  BookOpen,
  Plus,
  Check,
  Download, // 204 §E
} from "lucide-react";
import { RAD, FS } from "../lib/tokens";
import type { DocumentTemplate, DocumentTemplateVariant } from "../../shared/entities";
import type { TFn } from "../lib/taxonomyTypes";
import MenuSelect from "./MenuSelect"; // 154B §2
import { useConfirm } from "./useConfirmAndUndo";

const LANGS = [
  ["fr", "FR"],
  ["nl", "NL"],
  ["en", "EN"],
];

type DocTemplatesPanelProps = {
  toast?: any;
  collapsed?: any;
  onToggle?: any;
};

/* The served template plus the variants the API includes with it — the
   generated `DocumentTemplate` has no `variants` field because Prisma's scalar
   shape has none, and this row is rendered from the include. */
interface TemplateWithVariants extends DocumentTemplate {
  variants?: DocumentTemplateVariant[];
}

interface DocTemplateVariantSlotsProps {
  tpl: TemplateWithVariants;
  canEdit: boolean;
  busy: boolean;
  /* TYPED, and the first cut of this got the trade wrong. `any` here costs a
     warning apiece under eslint AND a count under `scripts/lint/ratchets.js`,
     which tracks `any` NODES separately and forbids a file to grow them —
     eslint headroom buys nothing there. A precise function type costs only the
     eslint side (base `no-unused-vars` counts every NAMED parameter of a
     function type, this config setting no ignore pattern), and this file has
     room for that. So: real types. */
  onUpload: (tpl: TemplateWithVariants, lang: string, file: File | undefined) => void;
  onRemove: (tpl: TemplateWithVariants, lang: string) => void;
  /** 204 §E — the third verb: re-download the stored file (the endpoint existed; the button did not) */
  onDownload: (tpl: TemplateWithVariants, lang: string, fileName: string) => void;
  t: TFn;
}

/* ── 170 §3e — THE PER-LANGUAGE SLOTS, EXTRACTED SO THE SETTINGS PANEL MOUNTS
 * THEM RATHER THAN FORKING THEM.
 *
 * §3e moves the pro-forma template rows into the Settings panel already called
 * "Pro Forma Template", and the instruction was explicit: reuse this row, do not
 * fork it. So it moves to module scope, markup verbatim, its two verbs arriving
 * as props, and BOTH callers mount the same component. A second copy of an
 * upload control is how one of them comes to accept a file the other refuses.
 *
 * Module scope rather than a nested function: a PascalCase component declared
 * inside a render body is Bug-A, and `scripts/lint-inline-components.js` fails
 * CI on exactly that.
 *
 * One slot per language. The upload control is a clearly-labeled, interactive
 * button (a hidden file input behind a styled <label>); the slot shows the
 * uploaded filename when set. Upload is a plain DB file upload — NOT
 * M365-dependent.
 */
export function DocTemplateVariantSlots({ tpl, canEdit, busy, onUpload, onRemove, onDownload, t }: Readonly<DocTemplateVariantSlotsProps>) {
  return (
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {LANGS.map(([code, label]) => {
          const v = (tpl.variants || []).find(
            (x: any) => x.language === code,
          );
          return (
            <div
              key={code}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 5,
                border: "1px solid var(--br)",
                borderRadius: RAD.r5,
                padding: "6px 8px",
                minWidth: 150,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  minWidth: 0,
                }}
              >
                <strong style={{ fontSize: FS.s10, flexShrink: 0 }}>
                  {label}
                </strong>
                {v ? (
                  <span
                    style={{
                      fontSize: FS.s10,
                      color: "var(--gn)",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 3,
                      minWidth: 0,
                    }}
                    title={`${v.fileName} · ${Math.round(v.size / 1024)}KB`}
                  >
                    <Check size={11} style={{ flexShrink: 0 }} />
                    <span
                      style={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: 130,
                      }}
                    >
                      {v.fileName}
                    </span>
                    {/* 204 §E — the download, BESIDE THE FILE it downloads and
                        not among the edit verbs below: reading back what was
                        uploaded is not an edit, and the server gates it on the
                        panel's own section key, which is what mounts this row.
                        The panel's icon-button idiom (the remove button's
                        `2px 5px` and a 10-px icon), ghost rather than danger. */}
                    <button
                      type="button"
                      className="btn btn-ghost"
                      style={{ padding: "2px 5px", flexShrink: 0 }}
                      onClick={() => {
                        onDownload(tpl, code, v.fileName);
                      }}
                      disabled={busy}
                      title={t("docTpl.download", { fallback: "Download" })}
                    >
                      <Download size={10} />
                    </button>
                  </span>
                ) : (
                  <span
                    style={{
                      fontSize: FS.s10,
                      color: "var(--tm)",
                      fontStyle: "italic",
                    }}
                  >
                    {t("docTpl.no_file", { fallback: "No file" })}
                  </span>
                )}
              </div>
              {canEdit && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                >
                  <label
                    className="btn btn-primary"
                    style={{
                      padding: "3px 9px",
                      fontSize: FS.s9_5,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <Upload size={10} />
                    {v
                      ? t("docTpl.replace", { fallback: "Replace" })
                      : t("docTpl.upload", {
                          fallback: "Upload .docx",
                        })}
                    <input
                      type="file"
                      accept=".docx"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = "";
                        onUpload(tpl, code, f);
                      }}
                      disabled={busy}
                    />
                  </label>
                  {v && (
                    <button
                      className="btn btn-danger"
                      style={{ padding: "2px 5px" }}
                      onClick={() => onRemove(tpl, code)}
                      disabled={busy}
                      title={t("common.remove", {
                        fallback: "Remove",
                      })}
                    >
                      <Trash2 size={10} />
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
  );
}

export default function DocTemplatesPanel({ toast, collapsed, onToggle }: DocTemplatesPanelProps) {
  const { t } = useTranslation();
  /* 206E §4.4 [RULED 2026-09-23] — THE EXPLANATION BESIDE EACH MARKER IS A
     SCREEN WORD, served through the dictionary like every other: the key is
     `docTpl.marker.<list>.<tag>` (list: scalar · loop · enum · invoice ·
     invoice_loop, the catalogue's five arrays), the English is the catalogue's
     own text (server/src/lib/docGenVariables.js — the fallback here), French
     and Dutch are seeded. The keys are COMPUTED, which the i18n gates cannot
     read — so recordings/206e-document-words checks that every catalogue entry
     has its three rows and that its English row IS the catalogue's text. */
  const markerDesc = (list: string, m: unknown): string => {
    const e = m as { tag?: string; desc?: string }; // a catalogue entry, off the `any` served list
    return (t as TFn)(`docTpl.marker.${list}.${e.tag ?? ""}`, { fallback: e.desc ?? "" });
  };
  const askConfirm = useConfirm();
  const { can, hidden, viewer } = usePermissions();
  // Phase auth-c2 Stage 3 — was lateral.settingsBusiness; now the panel's
  // own per-section key. Admin overlay still short-circuits — but P20f's hide
  // list is not a permission, so it qualifies that short-circuit.
  const canEdit =
    !hidden("settings.customLists.docTemplates") &&
    (!!viewer?.isAdmin || can("settings.customLists.docTemplates"));

  const [templates, setTemplates] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [vars, setVars] = useState<any>(null);
  const [showVars, setShowVars] = useState(false);
  const [form, setForm] = useState({
    code: "",
    name: "",
    kind: "prose",
    targetSubfolder: "",
    sortOrder: 0,
  });
  const [folderTpl, setFolderTpl] = useState<any[]>([]); // canonical case-folder subfolder names
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    getDocTemplates()
      .then((rows) => setTemplates(Array.isArray(rows) ? rows : []))
      .catch((e) => {
        toast?.(e?.message || t("docTpl.load_failed", { fallback: "Failed to load templates" }), "error");
        setTemplates([]);
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (!collapsed && templates === null && !loading) load();
  }, [collapsed]);
  // Canonical subfolder names come from the case-folder template in Settings,
  // so the enumeration target binding is picked (numbered vs unnumbered),
  // never typed/guessed.
  useEffect(() => {
    if (collapsed || folderTpl.length) return;
    (getFirmSettings() as Promise<{
      caseFolderTemplate?: Array<string | { name?: string }>;
    } | null>)
      .then((fs) =>
        setFolderTpl(
          // Phase 36 (B) — caseFolderTemplate entries are {role?, name} objects.
          Array.isArray(fs?.caseFolderTemplate)
            ? fs.caseFolderTemplate
                .map((s: any) => (typeof s === "string" ? s : s?.name))
                .filter((s: any) => typeof s === "string" && s.trim())
            : [],
        ),
      )
      .catch(() => {});
  }, [collapsed]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadVars = () => {
    if (vars) {
      setShowVars((s) => !s);
      return;
    }
    getDocTemplateVariables()
      .then((v) => {
        setVars(v);
        setShowVars(true);
      })
      .catch(() => {});
  };

  const create = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      toast?.(
        "⚠ " +
          t("docTpl.code_name_required", {
            fallback: "Code and name are required",
          }),
      );
      return;
    }
    setBusy(true);
    try {
      await createDocTemplate({
        code: form.code.trim(),
        name: form.name.trim(),
        kind: form.kind,
        targetSubfolder:
          form.kind === "enumeration" && form.targetSubfolder.trim()
            ? form.targetSubfolder.trim()
            : null,
        sortOrder: Number(form.sortOrder) || 0,
      });
      setForm({
        code: "",
        name: "",
        kind: "prose",
        targetSubfolder: "",
        sortOrder: 0,
      });
      load();
      toast?.(t("docTpl.created", { fallback: "Template created" }));
    } catch (e: any) {
      toast?.(e?.message || t("docTpl.create_failed", { fallback: "Create failed" }), "error");
    } finally {
      setBusy(false);
    }
  };

  /* 204 §E — the two sibling verbs typed to the slots' own prop types (the
     ratchet's room for the third verb was bought here, not by a new baseline). */
  const upload = async (tpl: TemplateWithVariants, lang: string, file: File | undefined) => {
    if (!file) return;
    if (!/\.docx$/i.test(file.name)) {
      toast?.(
        "⚠ " +
          t("docTpl.only_docx", { fallback: "Only .docx files are supported" }),
      );
      return;
    }
    setBusy(true);
    try {
      await uploadDocTemplateVariant(tpl.id, lang, file);
      load();
      toast?.(t("docTpl.uploaded", { fallback: "Template file uploaded" }));
    } catch (e: unknown) {
      toast?.((e instanceof Error && e.message) || t("docTpl.upload_failed", { fallback: "Upload failed" }), "error");
    } finally {
      setBusy(false);
    }
  };

  const removeVariant = async (tpl: TemplateWithVariants, lang: string) => {
    setBusy(true);
    try {
      await deleteDocTemplateVariant(tpl.id, lang);
      load();
    } catch (e: unknown) {
      toast?.((e instanceof Error && e.message) || t("docTpl.delete_failed", { fallback: "Delete failed" }), "error");
    } finally {
      setBusy(false);
    }
  };

  /* 204 §E — the stored file back to the lawyer, the shape of removeVariant's
     failure path: the server's reason, else the panel's own sentence. */
  const downloadVariant = async (tpl: TemplateWithVariants, lang: string, fileName: string) => {
    try {
      await downloadDocTemplateVariant(tpl.id, lang, fileName);
    } catch (e: unknown) {
      toast?.((e instanceof Error && e.message) || t("docTpl.download_failed", { fallback: "Download failed" }), "error");
    }
  };

  const removeTemplate = async (tpl: any) => {
    askConfirm({
      // 161C §3
      title: t("confirm.delete_template_title", { fallback: "Delete the template?" }),
      message: t("docTpl.confirm_delete", {
        fallback: "Delete this template and all its files?",
      }),
      confirmLabel: t("common.delete", { fallback: "Delete" }),
      onConfirm: async () => {
        setBusy(true);
        try {
          await deleteDocTemplate(tpl.id);
          load();
        } catch (e: any) {
          toast?.(e?.message || t("docTpl.delete_failed", { fallback: "Delete failed" }), "error");
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const setKind = async (tpl: any, kind: any) => {
    setBusy(true);
    try {
      await updateDocTemplate(tpl.id, { kind });
      load();
    } catch (e: any) {
      toast?.(e?.message || t("docTpl.update_failed", { fallback: "Update failed" }), "error");
    } finally {
      setBusy(false);
    }
  };
  const setTarget = async (tpl: any, targetSubfolder: any) => {
    setBusy(true);
    try {
      await updateDocTemplate(tpl.id, {
        targetSubfolder: targetSubfolder || null,
      });
      load();
    } catch (e: any) {
      toast?.(e?.message || t("docTpl.update_failed", { fallback: "Update failed" }), "error");
    } finally {
      setBusy(false);
    }
  };

  const lbl = {
    fontSize: FS.s9,
    color: "var(--tm)",
    textTransform: "uppercase",
    letterSpacing: 0.4,
  };

  // Template management is a firm-config surface — only business-settings
  // managers see it. Lawyers consume templates through the in-case picker.
  // (Placed after all hooks to respect rules-of-hooks.)
  if (!canEdit) return null;

  return (
    <div className="detail-card settings-pane" style={{ marginBottom: 10, order: 82 }} data-spane="docTpl">
      <h4 className="settings-pane-title" style={{ margin: 0 }} onClick={onToggle}>
        <span className="settings-chevron">{collapsed ? "▶" : "▼"}</span>
        {t("docTpl.panel_title", { fallback: "Document Templates" })}
      </h4>
      {!collapsed && (
        <>
          <p
            style={{
              fontSize: FS.s11,
              color: "var(--tm)",
              margin: "4px 0 10px",
              lineHeight: 1.5,
            }}
          >
            {t("docTpl.panel_desc", {
              fallback:
                "Upload Word (.docx) templates lawyers can merge case data into. Prose templates substitute scalar fields and party lists; enumeration templates build their body by walking a SharePoint subfolder. Author the template in Word using the canonical {markers} listed below.",
            })}
          </p>

          {/* Variable reference — 190 round 5 [P] addendum (Laurent,
              2026-09-16): opening the help list is not a deletion; the
              affirmative family, as the editor toggle. */}
          <button
            type="button"
            className="btn btn-primary"
            style={{
              padding: "3px 8px",
              fontSize: FS.s10,
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              marginBottom: 8,
            }}
            onClick={loadVars}
          >
            <BookOpen size={12} />{" "}
            {t("docTpl.show_markers", { fallback: "Marker reference" })}
          </button>
          {showVars && vars && (
            <div
              style={{
                border: "1px solid var(--br)",
                borderRadius: RAD.r6,
                padding: 8,
                marginBottom: 10,
                fontSize: FS.s10_5,
                lineHeight: 1.6,
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                {t("docTpl.scalars", { fallback: "Scalars" })}
              </div>
              {vars.scalars.map((s: any) => (
                <div key={s.tag}>
                  <code style={{ color: "var(--ac)" }}>{`{${s.tag}}`}</code> —{" "}
                  {markerDesc("scalar", s)}
                </div>
              ))}
              <div style={{ fontWeight: 600, margin: "8px 0 4px" }}>
                {t("docTpl.loops", {
                  fallback: "Loops (repeat a block per party)",
                })}
              </div>
              {vars.loops.map((l: any) => (
                <div key={l.tag} style={{ marginBottom: 4 }}>
                  <code
                    style={{ color: "var(--ac)" }}
                  >{`{#${l.tag}} … {/${l.tag}}`}</code>{" "}
                  — {markerDesc("loop", l)}
                  <div style={{ paddingLeft: 12, color: "var(--ts)" }}>
                    {l.fields.map((f: any) => `{${f.name}}`).join("  ")}
                  </div>
                </div>
              ))}
              <div style={{ fontWeight: 600, margin: "8px 0 4px" }}>
                {t("docTpl.enum_loops", {
                  fallback: "Enumeration (folder-walk body)",
                })}
              </div>
              {vars.enumerationLoops.map((l: any) => (
                <div key={l.tag} style={{ marginBottom: 2 }}>
                  <code style={{ color: "var(--ac)" }}>
                    {l.nested
                      ? `{#${l.tag}} … {/${l.tag}}`
                      : `{#${l.tag}} … {/${l.tag}}`}
                  </code>{" "}
                  — {markerDesc("enum", l)}
                </div>
              ))}
              {vars.invoiceScalars && vars.invoiceScalars.length > 0 && (
                <>
                  <div style={{ fontWeight: 600, margin: "8px 0 4px" }}>
                    {t("docTpl.invoice_scalars", {
                      fallback: "Invoice (pro forma) — scalars",
                    })}
                  </div>
                  {vars.invoiceScalars.map((s: any) => (
                    <div key={s.tag}>
                      <code
                        style={{ color: "var(--ac)" }}
                      >{`{${s.tag}}`}</code>{" "}
                      — {markerDesc("invoice", s)}
                    </div>
                  ))}
                </>
              )}
              {vars.invoiceLoops && vars.invoiceLoops.length > 0 && (
                <>
                  <div style={{ fontWeight: 600, margin: "8px 0 4px" }}>
                    {t("docTpl.invoice_loops", {
                      fallback: "Invoice (pro forma) — annex loop",
                    })}
                  </div>
                  {vars.invoiceLoops.map((l: any) => (
                    <div key={l.tag} style={{ marginBottom: 4 }}>
                      <code
                        style={{ color: "var(--ac)" }}
                      >{`{#${l.tag}} … {/${l.tag}}`}</code>{" "}
                      — {markerDesc("invoice_loop", l)}
                      <div style={{ paddingLeft: 12, color: "var(--ts)" }}>
                        {l.fields.map((f: any) => `{${f.name}}`).join("  ")}
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* Existing templates */}
          {loading && (
            <p style={{ fontSize: FS.s11, color: "var(--tm)" }}>
              {t("common.loading", { fallback: "Loading…" })}
            </p>
          )}
          {templates && templates.length === 0 && !loading && (
            <p
              style={{ fontSize: FS.s11, color: "var(--tm)", fontStyle: "italic" }}
            >
              {t("docTpl.none", { fallback: "No templates yet." })}
            </p>
          )}
          {templates &&
            templates.map((tpl: any) => (
              <div
                key={tpl.id}
                style={{
                  border: "1px solid var(--br)",
                  borderRadius: RAD.r6,
                  padding: 8,
                  marginBottom: 6,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 6,
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono',monospace",
                      fontWeight: 600,
                      color: "var(--ac)",
                    }}
                  >
                    {tpl.code}
                  </span>
                  <span style={{ flex: 1, fontSize: FS.s12 }}>{tpl.name}</span>
                  {canEdit && (
                    <MenuSelect
                      className="field-input"
                      style={{ width: 130, padding: "2px 4px", fontSize: FS.s10 }}
                      value={tpl.kind}
                      onChange={(v) => setKind(tpl, v)}
                      disabled={busy}
                      options={[
                        ["prose", t("docTpl.kind_prose", { fallback: "Prose pleading" })],
                        ["enumeration", t("docTpl.kind_enum", { fallback: "Enumeration" })],
                        ["invoice", t("docTpl.kind_invoice", { fallback: "Invoice (pro forma)" })],
                      ]}
                    />
                  )}
                  {canEdit && (
                    <button
                      className="btn btn-danger"
                      style={{ padding: "2px 5px" }}
                      title={t("common.delete", { fallback: "Delete" })}
                      onClick={() => removeTemplate(tpl)}
                      disabled={busy}
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
                {tpl.kind === "enumeration" && canEdit && (
                  <div className="field" style={{ marginBottom: 6 }}>
                    <label style={lbl}>
                      {t("docTpl.target_subfolder", {
                        fallback:
                          "Fixed target subfolder (optional — leave blank to let the lawyer pick at generation)",
                      })}
                    </label>
                    <MenuSelect
                      className="field-input"
                      style={{ padding: "3px 6px", fontSize: FS.s11 }}
                      value={tpl.targetSubfolder || ""}
                      onChange={(v) => setTarget(tpl, v)}
                      disabled={busy}
                      options={[
                        [
                          "",
                          t("docTpl.target_lawyer_picks", {
                            fallback: "— Lawyer picks at generation —",
                          }),
                        ] as const,
                        ...folderTpl.map((f) => [f, f] as const),
                        /* the stored subfolder, kept offerable when the firm's
                           template no longer lists it — this site's own 05m-b */
                        ...(tpl.targetSubfolder &&
                        !folderTpl.includes(tpl.targetSubfolder)
                          ? [[tpl.targetSubfolder, tpl.targetSubfolder] as const]
                          : []),
                      ]}
                    />
                  </div>
                )}
                <DocTemplateVariantSlots
                  tpl={tpl}
                  canEdit={canEdit}
                  busy={busy}
                  onUpload={upload}
                  onRemove={removeVariant}
                  onDownload={downloadVariant}
                  t={t}
                />
              </div>
            ))}

          {/* Create new */}
          {canEdit && (
            <div
              style={{
                borderTop: "1px solid var(--br)",
                marginTop: 8,
                paddingTop: 8,
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "90px 1fr 130px auto",
                  gap: 4,
                  alignItems: "end",
                }}
              >
                <div className="field">
                  <label style={lbl}>
                    {t("docTpl.code", { fallback: "Code" })}
                  </label>
                  <input
                    className="field-input"
                    style={{ padding: "3px 6px", fontSize: FS.s11 }}
                    placeholder="CCL"
                    value={form.code}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, code: e.target.value }))
                    }
                  />
                </div>
                <div className="field">
                  <label style={lbl}>
                    {t("docTpl.name", { fallback: "Name" })}
                  </label>
                  <input autoComplete="off" /* 207 §18 */
                    className="field-input no-contact-autofill"
                    style={{ padding: "3px 6px", fontSize: FS.s11 }}
                    placeholder={t("docTpl.name_ph", {
                      fallback: "Conclusions",
                    })}
                    value={form.name}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, name: e.target.value }))
                    }
                  />
                </div>
                <div className="field">
                  <label style={lbl}>
                    {t("docTpl.kind", { fallback: "Kind" })}
                  </label>
                  <MenuSelect
                    className="field-input"
                    style={{ padding: "3px 6px", fontSize: FS.s11 }}
                    value={form.kind}
                    onChange={(v) => setForm((p) => ({ ...p, kind: v }))}
                    options={[
                      ["prose", t("docTpl.kind_prose", { fallback: "Prose pleading" })],
                      ["enumeration", t("docTpl.kind_enum", { fallback: "Enumeration" })],
                      ["invoice", t("docTpl.kind_invoice", { fallback: "Invoice (pro forma)" })],
                    ]}
                  />
                </div>
                <button
                  className="btn btn-primary"
                  style={{
                    padding: "4px 10px",
                    fontSize: FS.s11,
                    whiteSpace: "nowrap",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                  }}
                  onClick={create}
                  disabled={busy}
                >
                  <Plus size={12} />
                  {t("common.add", { fallback: "Add" })}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
